import { Reducer } from 'redux';
import { IVisaPrepFormState, VisaPrepFormActionTypes } from './types';
import { StudentActionTypes, VQ } from '../types';
import { hasSpouseDetails, isPersonTravellingWith } from '../../../utilities/pages';

const initialState: IVisaPrepFormState = {
  hasBeenActivated: false,
  additionalPassports: [],
  otherUkVisas: [],
  tripsToUk: [],
  exclusionOrders: [],
  leaveToRemains: [],
  borderRefusals: [],
  deportations: [],
  children: [],
  otherTravellers: [],
  familyMembers: [],
  travelItems: [],
  isSectionStarted: false,
  isSectionComplete: false,
};

const reducer: Reducer<IVisaPrepFormState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS: {
      return {
        ...initialState,
        ...action.payload.student.visaPrepForm,
      };
    }
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS:
    case StudentActionTypes.UPDATE_CONFLICT_SUCCESS:
    case StudentActionTypes.UPDATE_OFFER_EVIDENCE_SUCCESS: {
      if (action.payload.visaPrepForm) {
        return {
          ...initialState,
          ...action.payload.visaPrepForm,
        };
      }
      return state;
    }
    case VisaPrepFormActionTypes.UPDATE_VPI_FIELD: {
      switch (action.payload.fieldName) {
        case VQ.HAS_DIFFERENT_CONTACT_DETAILS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.DIFF_CONTACT_ADDRESS]: '',
            [VQ.DIFF_CONTACT_CITY]: '',
            [VQ.DIFF_CONTACT_POSTCODE]: '',
            [VQ.DIFF_CONTACT_COUNTRY]: '',
          };
        }
        case VQ.HOME_OWNERSHIP_STATUS: {
          if (action.payload.value !== 'Other') {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
              [VQ.HOME_OWNERSHIP_STATUS_OTHER]: '',
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
          };
        }
        case VQ.HAS_TRAVELLED: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.TRAVEL_DETAILS]: [],
          };
        }
        case VQ.HAS_ARRIVAL_DATE: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.ARRIVAL_DATE]: '',
            [VQ.LENGTH_IN_UK_INTENTION_DD]: 0,
            [VQ.LENGTH_IN_UK_INTENTION_MM]: 0,
            [VQ.LENGTH_IN_UK_INTENTION_YY]: 0,
          };
        }
        case VQ.HAS_ADDITIONAL_PASSPORTS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.ADDITIONAL_PASSPORTS]: [],
          };
        }
        case VQ.HAS_OTHER_UK_VISAS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.OTHER_UK_VISAS]: [],
          };
        }
        case VQ.HAS_BEEN_TO_UK: {
          const { hasTravelledToUk } = action.payload.refValue;

          if (!action.payload.value && !hasTravelledToUk) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
              [VQ.HAS_NI_NUMBER]: undefined,
              [VQ.NI_NUMBER]: '',
              [VQ.HAS_RECEIVED_MEDICAL_TREATMENT]: undefined,
              [VQ.HAS_VOLUNTARILY_DEPARTED]: undefined,
              [VQ.HAS_EXCLUSION_ORDER]: undefined,
              [VQ.EXCLUSION_ORDERS]: [],
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
          };
        }
        case VQ.HAS_NI_NUMBER: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.NI_NUMBER]: '',
          };
        }
        case VQ.HAS_EXCLUSION_ORDER: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.EXCLUSION_ORDERS]: [],
          };
        }
        case VQ.HAS_BEEN_DEPORTED: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.DEPORTATIONS]: [],
          };
        }
        case VQ.MARITAL_STATUS: {
          if (hasSpouseDetails(action.payload.value)) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.SPOUSE_NATIONALITY]: '',
            [VQ.SPOUSE_SAME_NATIONALITY]: undefined,
            [VQ.SPOUSE_FAMILY_NAME]: '',
            [VQ.SPOUSE_GIVEN_NAME]: '',
            [VQ.SPOUSE_DOB]: '',
            [VQ.SPOUSE_LIVING_WITH]: undefined,
            [VQ.SPOUSE_TRAVELLING_WITH]: undefined,
            [VQ.SPOUSE_UK_VISA]: undefined,
            [VQ.SPOUSE_HAS_PASSPORT]: undefined,
            [VQ.SPOUSE_PASSPORT_NUMBER]: '',
          };
        }
        case VQ.SPOUSE_TRAVELLING_WITH:
          if (isPersonTravellingWith(action.payload.value)) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.SPOUSE_UK_VISA]: undefined,
            [VQ.SPOUSE_HAS_PASSPORT]: undefined,
            [VQ.SPOUSE_PASSPORT_NUMBER]: '',
          };

        case VQ.SPOUSE_HAS_PASSPORT:
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.SPOUSE_PASSPORT_NUMBER]: '',
          };

        case VQ.MOTHERS_PERSONAL_DETAILS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.MOTHER_NATIONALITY]: '',
            [VQ.MOTHER_SAME_NATIONALITY]: undefined,
            [VQ.MOTHER_FAMILY_NAME]: '',
            [VQ.MOTHER_GIVEN_NAME]: '',
            [VQ.MOTHER_DOB]: '',
            [VQ.MOTHER_PLACE_OF_BIRTH]: '',
            [VQ.MOTHER_TRAVELLING_WITH]: undefined,
            [VQ.MOTHER_UK_VISA]: undefined,
            [VQ.MOTHER_HAS_PASSPORT]: undefined,
            [VQ.MOTHER_PASSPORT_NUMBER]: '',
          };
        }
        case VQ.MOTHER_TRAVELLING_WITH:
          if (isPersonTravellingWith(action.payload.value)) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.MOTHER_UK_VISA]: undefined,
            [VQ.MOTHER_HAS_PASSPORT]: undefined,
            [VQ.MOTHER_PASSPORT_NUMBER]: '',
          };

        case VQ.MOTHER_HAS_PASSPORT:
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.MOTHER_PASSPORT_NUMBER]: '',
          };

        case VQ.GUARDIANS_PERSONAL_DETAILS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.GUARDIAN_NATIONALITY]: '',
            [VQ.GUARDIAN_SAME_NATIONALITY]: undefined,
            [VQ.GUARDIAN_FAMILY_NAME]: '',
            [VQ.GUARDIAN_GIVEN_NAME]: '',
            [VQ.GUARDIAN_DOB]: '',
            [VQ.GUARDIAN_PLACE_OF_BIRTH]: '',
            [VQ.GUARDIAN_TRAVELLING_WITH]: undefined,
            [VQ.GUARDIAN_UK_VISA]: undefined,
            [VQ.GUARDIAN_HAS_PASSPORT]: undefined,
            [VQ.GUARDIAN_PASSPORT_NUMBER]: '',
          };
        }
        case VQ.GUARDIAN_TRAVELLING_WITH:
          if (isPersonTravellingWith(action.payload.value)) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.GUARDIAN_UK_VISA]: undefined,
            [VQ.GUARDIAN_HAS_PASSPORT]: undefined,
            [VQ.GUARDIAN_PASSPORT_NUMBER]: '',
          };

        case VQ.GUARDIAN_HAS_PASSPORT:
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.GUARDIAN_PASSPORT_NUMBER]: '',
          };

        case VQ.FATHERS_PERSONAL_DETAILS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.FATHER_NATIONALITY]: '',
            [VQ.FATHER_SAME_NATIONALITY]: undefined,
            [VQ.FATHER_FAMILY_NAME]: '',
            [VQ.FATHER_GIVEN_NAME]: '',
            [VQ.FATHER_DOB]: '',
            [VQ.FATHER_PLACE_OF_BIRTH]: '',
            [VQ.FATHER_TRAVELLING_WITH]: undefined,
            [VQ.FATHER_UK_VISA]: undefined,
            [VQ.FATHER_HAS_PASSPORT]: undefined,
            [VQ.FATHER_PASSPORT_NUMBER]: '',
          };
        }
        case VQ.FATHER_TRAVELLING_WITH:
          if (isPersonTravellingWith(action.payload.value)) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.FATHER_UK_VISA]: undefined,
            [VQ.FATHER_HAS_PASSPORT]: undefined,
            [VQ.FATHER_PASSPORT_NUMBER]: '',
          };

        case VQ.FATHER_HAS_PASSPORT:
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.FATHER_PASSPORT_NUMBER]: '',
          };

        case VQ.HAS_DEPENDANT_CHILDREN: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.CHILDREN_LIVING_WITH_YOU]: undefined,
            [VQ.CONTACT_ADDRESS]: '',
            [VQ.CONTACT_CITY]: '',
            [VQ.CONTACT_POSTCODE]: '',
            [VQ.CONTACT_COUNTRY]: '',
            [VQ.CONTACT_NUMBER]: '',
            [VQ.CHILD_DETAILS]: [],
          };
        }
        case VQ.CHILDREN_LIVING_WITH_YOU:
          if (action.payload.value === false) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.CONTACT_ADDRESS]: '',
            [VQ.CONTACT_CITY]: '',
            [VQ.CONTACT_POSTCODE]: '',
            [VQ.CONTACT_COUNTRY]: '',
            [VQ.CONTACT_NUMBER]: '',
          };

        case VQ.UK_FAMILY: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.STAYING_WITH_FAMILY]: undefined,
            [VQ.FAMILY_MEMBERS]: [],
            [VQ.FAMILY_CONTACT_ADDRESS]: '',
            [VQ.FAMILY_CONTACT_CITY]: '',
            [VQ.FAMILY_CONTACT_COUNTRY]: '',
            [VQ.FAMILY_CONTACT_NUMBER]: '',
          };
        }
        case VQ.STAYING_WITH_FAMILY: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.FAMILY_MEMBERS]: [],
            [VQ.FAMILY_CONTACT_ADDRESS]: '',
            [VQ.FAMILY_CONTACT_CITY]: '',
            [VQ.FAMILY_CONTACT_COUNTRY]: '',
            [VQ.FAMILY_CONTACT_NUMBER]: '',
          };
        }
        case VQ.TRAVELLING_WITH_OTHERS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.OTHER_TRAVELLERS]: [],
          };
        }
        case VQ.HAS_OTHER_CONTACT_ADDRESS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.OTHER_CONTACT_ADDRESS]: '',
            [VQ.OTHER_CONTACT_POSTCODE]: '',
            [VQ.OTHER_CONTACT_CITY]: '',
            [VQ.OTHER_CONTACT_COUNTRY]: '',
            [VQ.OTHER_CONTACT_NUMBER]: '',
          };
        }
        case VQ.HAS_RECEIVED_CAS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.STUDIES_CONFIRMATION_NUMBER]: '',
            [VQ.COURSE_END_DATE]: '',
            [VQ.STUDY_ADDRESS]: '',
          };
        }
        case VQ.QUALIFICATION_AS_PROOF_OF_ENGLISH: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.QUALIFICATION_AND_COUNTRY]: '',
          };
        }
        case VQ.APPLIED_THROUGH_UCAS: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.UCAS_PIN]: '',
          };
        }
        case VQ.ACCOMMODATION_FEES_PAID: {
          if (action.payload.value) {
            return {
              ...state,
              [action.payload.fieldName]: action.payload.value,
            };
          }
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
            [VQ.ACCOMMODATION_FEES]: 0,
          };
        }
        default: {
          return {
            ...state,
            [action.payload.fieldName]: action.payload.value,
          };
        }
      }
    }
    default: {
      return state;
    }
  }
};

export { reducer as visaPrepFormReducer };
